import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import AnimatedModal from "@/components/base/AnimatedModal";

interface JobData {
  id: string;
  title: string;
  location: string;
  salary: string;
  type: string;
  category: string;
  description: string;
  detail: string;
  requirements: string[];
  benefits: string[];
  status: string;
  is_expired: boolean;
  khu_vuc?: string;
  employer_email?: string;
  employer_phone?: string;
}

interface EditJobModalProps {
  isOpen: boolean;
  onClose: () => void;
  job: JobData | null;
  onSaved: () => void;
}

const jobTypes = [
  { value: "full-time", label: "Toàn thời gian" },
  { value: "part-time", label: "Bán thời gian" },
  { value: "contract", label: "Hợp đồng" },
];

const categories = [
  "Khu CN, Sản xuất, May mặc",
  "Việc làm phổ thông, Giúp việc",
  "Lái xe, Phụ xe, Giao hàng",
  "Partime, Sinh viên",
  "Hành chính nhân sự, kế toán, kiểm toán",
  "Quản lý, Kinh doanh, Sale",
  "CNTT, Kỹ sư, Kỹ thuật",
  "Y tế, Dược, Sinh hóa",
  "Xây dựng, Thiết kế, Kiến trúc",
  "Dịch vụ, Du lịch, Nhà hàng, Khách sạn",
  "Nông nghiệp, Công nghệ Thực phẩm",
  "Việc online, Chốt đơn, Telesale, Đóng hàng",
  "Freelancer",
  "Khác",
];

const provinces = [
  { value: "hà nội", label: "Hà Nội" },
  { value: "tp. hồ chí minh", label: "TP. Hồ Chí Minh" },
  { value: "an giang", label: "An Giang" },
  { value: "bắc ninh", label: "Bắc Ninh" },
  { value: "cà mau", label: "Cà Mau" },
  { value: "cao bằng", label: "Cao Bằng" },
  { value: "cần thơ", label: "Cần Thơ" },
  { value: "đà nẵng", label: "Đà Nẵng" },
  { value: "đăk lawk", label: "Đắk Lắk" },
  { value: "điện biên", label: "Điện Biên" },
  { value: "đồng nai", label: "Đồng Nai" },
  { value: "đồng tháp", label: "Đồng Tháp" },
  { value: "gia lai", label: "Gia Lai" },
  { value: "hà tĩnh", label: "Hà Tĩnh" },
  { value: "hải phòng", label: "Hải Phòng" },
  { value: "huế", label: "Huế" },
  { value: "hưng yên", label: "Hưng Yên" },
  { value: "khánh hòa", label: "Khánh Hòa" },
  { value: "lai châu", label: "Lai Châu" },
  { value: "lâm đồng", label: "Lâm Đồng" },
  { value: "lạng sơn", label: "Lạng Sơn" },
  { value: "lào cai", label: "Lào Cai" },
  { value: "nghệ an", label: "Nghệ An" },
  { value: "ninh bình", label: "Ninh Bình" },
  { value: "phú thọ", label: "Phú Thọ" },
  { value: "quảng ngãi", label: "Quảng Ngãi" },
  { value: "quang ninh", label: "Quảng Ninh" },
  { value: "quảng trị", label: "Quảng Trị" },
  { value: "sơn la", label: "Sơn La" },
  { value: "tây ninh", label: "Tây Ninh" },
  { value: "thái nguyên", label: "Thái Nguyên" },
  { value: "thanh hóa", label: "Thanh Hóa" },
  { value: "tuyên quang", label: "Tuyên Quang" },
  { value: "vĩnh long", label: "Vĩnh Long" },
];

export default function EditJobModal({ isOpen, onClose, job, onSaved }: EditJobModalProps) {
  const [title, setTitle] = useState("");
  const [khuVuc, setKhuVuc] = useState("");
  const [location, setLocation] = useState("");
  const [salary, setSalary] = useState("");
  const [type, setType] = useState("full-time");
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [description, setDescription] = useState("");
  const [detail, setDetail] = useState("");
  const [requirements, setRequirements] = useState<string[]>([""]);
  const [benefits, setBenefits] = useState<string[]>([""]);
  const [employerEmail, setEmployerEmail] = useState("");
  const [employerPhone, setEmployerPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [expiring, setExpiring] = useState(false);
  const [toast, setToast] = useState<{ type: "success" | "error"; message: string } | null>(null);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);

  useEffect(() => {
    if (job && isOpen) {
      setTitle(job.title);
      setLocation(job.location);
      setSalary(job.salary);
      setType(job.type);
      setKhuVuc(job.khu_vuc || "");
      setEmployerEmail(job.employer_email || "");
      setEmployerPhone(job.employer_phone || "");

      const parsedCategories = job.category
        ? job.category.split(", ").filter((c: string) => c.trim() !== "")
        : [];
      setSelectedCategories(parsedCategories);

      setDescription(job.description);
      setDetail(job.detail || "");
      setRequirements(job.requirements?.length > 0 ? job.requirements : [""]);
      setBenefits(job.benefits?.length > 0 ? job.benefits : [""]);
      setToast(null);
    }
  }, [job, isOpen]);

  const handleRequirementChange = (index: number, value: string) => {
    const updated = [...requirements];
    updated[index] = value;
    setRequirements(updated);
  };

  const addRequirement = () => setRequirements([...requirements, ""]);
  const removeRequirement = (index: number) => {
    if (requirements.length <= 1) return;
    setRequirements(requirements.filter((_, i) => i !== index));
  };

  const handleBenefitChange = (index: number, value: string) => {
    const updated = [...benefits];
    updated[index] = value;
    setBenefits(updated);
  };

  const addBenefit = () => setBenefits([...benefits, ""]);
  const removeBenefit = (index: number) => {
    if (benefits.length <= 1) return;
    setBenefits(benefits.filter((_, i) => i !== index));
  };

  const toggleCategory = (cat: string) => {
    setSelectedCategories((prev) =>
      prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]
    );
  };

  const showToast = (type: "success" | "error", message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 4000);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!job) return;

    if (!title.trim() || !location.trim() || !salary.trim() || !description.trim()) {
      showToast("error", "Vui lòng điền đầy đủ các trường bắt buộc.");
      return;
    }

    if (!khuVuc) {
      showToast("error", "Vui lòng chọn Khu vực.");
      return;
    }

    if (selectedCategories.length === 0) {
      showToast("error", "Vui lòng chọn ít nhất một Danh mục.");
      return;
    }

    setSubmitting(true);
    const cleanRequirements = requirements.filter((r) => r.trim() !== "");
    const cleanBenefits = benefits.filter((b) => b.trim() !== "");
    const categoryStr = selectedCategories.join(", ");

    const selectedProvince = provinces.find((p) => p.value === khuVuc);
    const locationKey = khuVuc;

    const { error } = await supabase
      .from("vietnamcv_jobs")
      .update({
        title: title.trim(),
        location: location.trim(),
        khu_vuc: selectedProvince?.label || khuVuc,
        location_key: locationKey,
        salary: salary.trim(),
        type,
        category: categoryStr,
        description: description.trim(),
        detail: detail.trim() || description.trim(),
        requirements: cleanRequirements.length > 0 ? cleanRequirements : ["Không yêu cầu kinh nghiệm"],
        benefits: cleanBenefits.length > 0 ? cleanBenefits : ["Lương thỏa thuận"],
        status: "pending",
        rejection_reason: null,
        employer_email: employerEmail.trim() || null,
        employer_phone: employerPhone.trim() || null,
      })
      .eq("id", job.id);

    if (error) {
      showToast("error", "Lỗi khi cập nhật: " + error.message);
      setSubmitting(false);
      return;
    }

    showToast("success", "Tin đã được gửi lại cho admin duyệt!");
    setTimeout(() => {
      onSaved();
      onClose();
    }, 1000);
    setSubmitting(false);
  };

  const handleMarkExpired = async () => {
    if (!job) return;

    setExpiring(true);
    const { error } = await supabase
      .from("vietnamcv_jobs")
      .update({ is_expired: true, status: job.status === "pending" ? "pending" : job.status })
      .eq("id", job.id);

    if (error) {
      showToast("error", "Lỗi khi gắn nhãn hết hạn: " + error.message);
      setExpiring(false);
      return;
    }

    showToast("success", "Tin đã được gắn nhãn hết hạn.");
    setTimeout(() => {
      onSaved();
      onClose();
    }, 1000);
    setExpiring(false);
  };

  const handleClose = () => {
    setToast(null);
    setShowCategoryDropdown(false);
    onClose();
  };

  if (!job) return null;

  return (
    <AnimatedModal isOpen={isOpen} onClose={handleClose} variant="fade-up" closeOnBackdrop={false}>
      <div className="relative w-[92vw] max-w-2xl bg-background-50 rounded-2xl border border-background-200/70 shadow-2xl max-h-[85vh] flex flex-col">
        {toast && (
          <div className={`absolute top-4 right-4 z-50 flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium animate-fade-in-down ${
            toast.type === "success" ? "bg-green-50 text-green-800 border border-green-200" : "bg-red-50 text-red-800 border border-red-200"
          }`}>
            <i className={`${toast.type === "success" ? "ri-checkbox-circle-line" : "ri-error-warning-line"} text-base`}></i>
            {toast.message}
          </div>
        )}

        <div className="flex items-center justify-between px-6 py-4 border-b border-background-100 flex-shrink-0">
          <div>
            <h2 className="text-lg font-heading font-bold text-foreground-950">Chỉnh sửa tin tuyển dụng</h2>
            <p className="text-xs text-foreground-500 mt-0.5">
              {job.status === "approved" && "Sau khi lưu, tin sẽ được gửi lại cho admin duyệt"}
              {job.status === "pending" && "Tin đang chờ duyệt — sau khi lưu vẫn ở trạng thái chờ duyệt"}
              {job.status === "rejected" && "Sau khi lưu, tin sẽ được gửi lại cho admin duyệt"}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer text-foreground-500"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <form onSubmit={handleSave} className="p-6 space-y-5 overflow-y-auto flex-1">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-foreground-800 mb-1.5">
                Tiêu đề tin tuyển dụng <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full px-4 py-3 text-sm bg-background-100 border border-background-200 rounded-xl text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-foreground-800 mb-1.5">
                Khu vực <span className="text-red-500">*</span>
              </label>
              <select
                value={khuVuc}
                onChange={(e) => setKhuVuc(e.target.value)}
                className="w-full px-4 py-3 text-sm bg-background-100 border border-background-200 rounded-xl text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all cursor-pointer"
              >
                <option value="">-- Chọn tỉnh/thành phố --</option>
                {provinces.map((p) => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-foreground-800 mb-1.5">
                Địa điểm làm việc <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="VD: Số 123 Nguyễn Huệ, Quận 1, TP.HCM"
                className="w-full px-4 py-3 text-sm bg-background-100 border border-background-200 rounded-xl text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-foreground-800 mb-1.5">
                Mức lương <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={salary}
                onChange={(e) => setSalary(e.target.value)}
                className="w-full px-4 py-3 text-sm bg-background-100 border border-background-200 rounded-xl text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-foreground-800 mb-1.5">Loại công việc</label>
              <div className="flex gap-2 bg-background-100 rounded-xl p-1 border border-background-200">
                {jobTypes.map((jt) => (
                  <button
                    key={jt.value}
                    type="button"
                    onClick={() => setType(jt.value)}
                    className={`flex-1 py-2 text-xs font-semibold rounded-lg transition-all cursor-pointer whitespace-nowrap ${
                      type === jt.value ? "bg-background-50 text-foreground-950" : "text-foreground-500 hover:text-foreground-700"
                    }`}
                  >
                    {jt.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-foreground-800 mb-1.5">
                Danh mục <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                  className="w-full px-4 py-3 text-sm bg-background-100 border border-background-200 rounded-xl text-left focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all cursor-pointer flex items-center justify-between"
                >
                  <span className={selectedCategories.length > 0 ? "text-foreground-900" : "text-foreground-400"}>
                    {selectedCategories.length > 0
                      ? `Đã chọn ${selectedCategories.length} danh mục`
                      : "-- Chọn danh mục --"}
                  </span>
                  <i className={`ri-arrow-down-s-line text-foreground-400 transition-transform duration-200 ${showCategoryDropdown ? "rotate-180" : ""}`}></i>
                </button>
                {showCategoryDropdown && (
                  <div className="absolute z-20 mt-1 w-full bg-background-50 border border-background-200 rounded-xl shadow-sm max-h-72 overflow-y-auto">
                    {categories.map((cat) => (
                      <label
                        key={cat}
                        className="flex items-center gap-3 px-4 py-2.5 hover:bg-background-100 cursor-pointer transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={selectedCategories.includes(cat)}
                          onChange={() => toggleCategory(cat)}
                          className="w-4 h-4 rounded border-background-300 text-primary-500 focus:ring-primary-400 cursor-pointer"
                        />
                        <span className="text-sm text-foreground-800">{cat}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              {selectedCategories.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {selectedCategories.map((cat) => (
                    <span
                      key={cat}
                      className="inline-flex items-center gap-1 px-2.5 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full"
                    >
                      {cat}
                      <button
                        type="button"
                        onClick={() => toggleCategory(cat)}
                        className="w-4 h-4 flex items-center justify-center hover:text-primary-900 cursor-pointer"
                      >
                        <i className="ri-close-line text-xs"></i>
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-semibold text-foreground-800 mb-1.5">
                Email liên hệ
              </label>
              <input
                type="email"
                value={employerEmail}
                onChange={(e) => setEmployerEmail(e.target.value)}
                placeholder="VD: hr@congty.com"
                className="w-full px-4 py-3 text-sm bg-background-100 border border-background-200 rounded-xl text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-foreground-800 mb-1.5">
                Số điện thoại liên hệ
              </label>
              <input
                type="tel"
                value={employerPhone}
                onChange={(e) => setEmployerPhone(e.target.value)}
                placeholder="VD: 0987654321"
                className="w-full px-4 py-3 text-sm bg-background-100 border border-background-200 rounded-xl text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-foreground-800 mb-1.5">
              Mô tả ngắn <span className="text-red-500">*</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={500}
              rows={3}
              className="w-full px-4 py-3 text-sm bg-background-100 border border-background-200 rounded-xl text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
            />
            <p className="text-xs text-foreground-400 mt-1 text-right">{description.length}/500</p>
          </div>

          <div>
            <label className="block text-sm font-semibold text-foreground-800 mb-1.5">Mô tả chi tiết</label>
            <textarea
              value={detail}
              onChange={(e) => setDetail(e.target.value)}
              maxLength={2000}
              rows={4}
              className="w-full px-4 py-3 text-sm bg-background-100 border border-background-200 rounded-xl text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
            />
            <p className="text-xs text-foreground-400 mt-1 text-right">{detail.length}/2000</p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-semibold text-foreground-800">Yêu cầu ứng viên</label>
            </div>
            <div className="space-y-2">
              {requirements.map((req, i) => (
                <div key={i} className="flex items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center shrink-0 text-foreground-400">
                    <i className="ri-checkbox-blank-circle-line text-[10px]"></i>
                  </div>
                  <input
                    type="text"
                    value={req}
                    onChange={(e) => handleRequirementChange(i, e.target.value)}
                    placeholder={`Yêu cầu ${i + 1}...`}
                    className="flex-1 px-3 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                  />
                  {requirements.length > 1 && (
                    <button type="button" onClick={() => removeRequirement(i)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-foreground-400 hover:text-red-500 transition-colors cursor-pointer shrink-0">
                      <i className="ri-close-line"></i>
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div className="flex justify-end mt-3">
              <button type="button" onClick={addRequirement} className="flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-700 cursor-pointer whitespace-nowrap">
                <i className="ri-add-line text-sm"></i> Thêm
              </button>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-semibold text-foreground-800">Quyền lợi</label>
            </div>
            <div className="space-y-2">
              {benefits.map((ben, i) => (
                <div key={i} className="flex items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center shrink-0 text-green-500">
                    <i className="ri-check-line text-sm"></i>
                  </div>
                  <input
                    type="text"
                    value={ben}
                    onChange={(e) => handleBenefitChange(i, e.target.value)}
                    placeholder={`Quyền lợi ${i + 1}...`}
                    className="flex-1 px-3 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                  />
                  {benefits.length > 1 && (
                    <button type="button" onClick={() => removeBenefit(i)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-foreground-400 hover:text-red-500 transition-colors cursor-pointer shrink-0">
                      <i className="ri-close-line"></i>
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div className="flex justify-end mt-3">
              <button type="button" onClick={addBenefit} className="flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-700 cursor-pointer whitespace-nowrap">
                <i className="ri-add-line text-sm"></i> Thêm
              </button>
            </div>
          </div>

          <div className="flex items-center gap-3 pt-2 border-t border-background-100">
            <button
              type="button"
              onClick={handleMarkExpired}
              disabled={expiring || job.is_expired}
              className={`px-4 py-3 text-sm font-medium rounded-xl transition-colors cursor-pointer whitespace-nowrap ${
                job.is_expired
                  ? "bg-background-200 text-foreground-400 cursor-not-allowed"
                  : "bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100"
              }`}
            >
              {expiring ? (
                <span className="flex items-center gap-2">
                  <i className="ri-loader-4-line animate-spin"></i> Đang xử lý...
                </span>
              ) : job.is_expired ? (
                <span className="flex items-center gap-2">
                  <i className="ri-timer-line"></i> Đã hết hạn
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <i className="ri-timer-line"></i> Gắn nhãn hết hạn
                </span>
              )}
            </button>
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-4 py-3 text-sm font-medium text-foreground-600 bg-background-100 hover:bg-background-200 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
            >
              Hủy
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 px-4 py-3 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-xl transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin"></i> Đang lưu...
                </span>
              ) : (
                "Lưu & Gửi duyệt"
              )}
            </button>
          </div>
        </form>
      </div>
    </AnimatedModal>
  );
}